import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import useRenditionStore from './rendition';
import useSettingStore from './setting';
import useNavigationStore from './navigation';
import { Track, VideoPosition, VideoSize } from '../types';

type MediaState = {
  currentPageTracks: Track[];
  spineItemsTracks: Track[][];
  lastPlayedBeginTime?: number;
  noMediaPageSkip: any;
  preferredLanguage?: string;
  audioLanguages: string[];
  videoLanguages: string[];
  otherTextLanguagesCodes: string[];
  pictureInPicture: string[];
  mainLanguage?: string;
  secondaryLanguage?: string;
  videoPosition: VideoPosition;
  videoSize: VideoSize;
  currentPlayerTime: number;
  audioDuration: number;
};

const getDefaultPipSize = (): VideoSize => {
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const w = Math.round(Math.min(640, viewportW * 0.9));
  return { w, h: Math.round(w * (360 / 640)) };
};

const defaultMediaState: MediaState = {
  currentPageTracks: [],
  spineItemsTracks: [],
  lastPlayedBeginTime: undefined,
  noMediaPageSkip: null,
  preferredLanguage: undefined,
  audioLanguages: [],
  otherTextLanguagesCodes: [],
  videoLanguages: [],
  pictureInPicture: [],
  mainLanguage: undefined,
  secondaryLanguage: undefined,
  videoPosition: { x: 0, y: 64 },
  videoSize: getDefaultPipSize(),
  currentPlayerTime: 0,
  audioDuration: 0,
};

const useMediaStore = defineStore('book-player-media', () => {
  const renditionStore = useRenditionStore();
  const { goToNextSpine, doWithAutoPageTurnTimeout } = renditionStore;
  const { hasAutoPageTurn, isAutoFollowPlayback } = storeToRefs(useSettingStore());
  const { setAudioTrackPosition } = useNavigationStore();

  const mediaState = ref<MediaState>({ ...defaultMediaState });

  const currentTrack = ref<Track | undefined>();
  const mediaAvailable = ref(true);
  const playing = ref(false);
  const videoLanguage = ref(false);
  const requestedPlayerPosition = ref<number>();
  const lastTrack = ref(false);
  const isUserChangingTrack = ref(false);
  const skipFeedback = ref<string | undefined>(undefined);
  const audioScrubbingTime = ref<number | undefined>(undefined);
  const activeHighlightCfi = ref<string | null>(null);
  const totalContinuousBookAudioDuration = ref<number>(0);
  const continuousBookAudioTrackTimings = ref<Map<string, { start: number; end: number }>>(new Map());

  const isPlaying = computed(() => playing.value);
  const isCurrentTrack = computed(() => currentTrack.value);
  const hasMediaAvailable = computed(() => mediaAvailable.value);
  const mainLanguage = computed(() => mediaState.value.mainLanguage);
  const secondaryLanguage = computed(() => mediaState.value.secondaryLanguage);
  const preferredLanguage = computed(() => mediaState.value.preferredLanguage);
  const audioLanguage = computed(() =>
    mediaState.value.preferredLanguage ? mediaState.value.preferredLanguage : mediaState.value.mainLanguage,
  );
  const audioLanguages = computed(() => mediaState.value.audioLanguages);

  /**
   * True when what is playing is the book's own SMIL narration rather than a secondary-language
   * dub. The two have different media layouts: SMIL narration is one clip per sentence, a dub is
   * one media file per page played end-to-end, so only the former advances sentence by sentence.
   *
   * mainLanguage comes from content-registry metadata (`bookInformation.languages.mainLanguage`),
   * not from the EPUB, and is regularly missing. Comparing the two languages directly then made a
   * plain SMIL book look like a dub: sentence advance never ran, and with it neither did auto page
   * turn. A book that never declares a main language cannot be playing a dub of it, so treat an
   * absent mainLanguage as a match.
   */
  const isPlayingMainLanguage = computed(() => !mainLanguage.value || mainLanguage.value === audioLanguage.value);

  const isLastTrack = computed(() => lastTrack.value);
  const currentPlayerTime = computed(() => mediaState.value.currentPlayerTime);
  const audioDuration = computed(() => mediaState.value.audioDuration);

  const isAtStartOfMedia = computed(() => currentPlayerTime.value === 0);
  const isAtEndOfMedia = computed(() => {
    if (audioDuration.value === 0) return false;
    return currentPlayerTime.value >= audioDuration.value;
  });

  const videoLanguages = computed(() => mediaState.value.videoLanguages);
  const otherTextLanguagesCodes = computed(() => mediaState.value.otherTextLanguagesCodes);
  const isVideoLanguage = computed(() => videoLanguage.value);
  const pictureInPicture = computed(() => mediaState.value.pictureInPicture);
  const hasPictureInPicture = computed(() => pictureInPicture.value.includes(preferredLanguage.value || ''));
  const videoPosition = computed(() => mediaState.value.videoPosition);
  const videoSize = computed(() => mediaState.value.videoSize);
  const getNextTrackOnPage = computed(() => {
    if (currentTrack.value) {
      const currentIndex = mediaState.value.currentPageTracks.findIndex(
        (track) => track.text.src === currentTrack.value?.text.src && track.audio.src === currentTrack.value?.audio.src,
      );
      return mediaState.value.currentPageTracks[currentIndex + 1];
    }
    return mediaState.value.currentPageTracks[0];
  });
  const getPrevTrackOnPage = computed(() => {
    if (currentTrack.value) {
      const currentIndex = mediaState.value.currentPageTracks.findIndex(
        (track) => track.text.src === currentTrack.value?.text.src && track.audio.src === currentTrack.value?.audio.src,
      );
      return mediaState.value.currentPageTracks[currentIndex - 1];
    }
    return mediaState.value.currentPageTracks[0];
  });
  const currentPageTracks = computed(() => mediaState.value.currentPageTracks);
  const spineItemsTracks = computed(() => mediaState.value.spineItemsTracks || []);
  const lastPlayedBeginTime = computed(() => mediaState.value.lastPlayedBeginTime);

  const getUniqueNextTrackOnPage = computed(() => {
    if (currentTrack.value) {
      const compareFn = (track: Track) => track.audio.src;
      const uniqueSrc = [...new Set(mediaState.value.currentPageTracks.map((track: Track) => track.audio.src))];
      const uniqueTracks: Track[] = [];

      uniqueSrc.forEach((src) => {
        const sameUrlTracks = mediaState.value.currentPageTracks.filter((track: Track) => track.audio.src === src);
        const first = sameUrlTracks[0];
        const last = sameUrlTracks[sameUrlTracks.length - 1];

        /*
         Collapse every clip that shares one media file into a single track spanning the whole
         file - that is the shape secondary-language media has, one file per page played
         end-to-end. This used to assign onto first.audio directly, which mutated the Track
         objects the SMIL store hands out from its per-spine cache: the first clip of the page
         permanently inherited the last clip's clipEnd, so once this ran the page was treated as
         one long clip for every later reader of that cache too.
         */
        uniqueTracks.push({
          ...first,
          audio: { ...first.audio, clipEnd: last.audio.clipEnd },
        });
      });

      return uniqueTracks[uniqueTracks.map(compareFn).indexOf(compareFn(currentTrack.value)) + 1] || null;
    }
    return mediaState.value.currentPageTracks[0];
  });

  const isCurrentTrackOnCurrentPage = computed(() => {
    const compareFn = (track?: Track) => track && track.audio.url;

    return mediaState.value.currentPageTracks.map(compareFn).includes(compareFn(currentTrack.value));
  });

  const isCurrentClipOnCurrentPage = computed(() => {
    if (!currentTrack.value) return false;
    const {
      audio: { src: audioSrc, clipBegin, clipEnd },
      text: { src: textSrc },
    } = currentTrack.value;
    return mediaState.value.currentPageTracks.some(
      (t) =>
        t.audio.src === audioSrc &&
        t.audio.clipBegin === clipBegin &&
        t.audio.clipEnd === clipEnd &&
        t.text.src === textSrc,
    );
  });

  const setMediaAvailable = (hasMediaAvailable: boolean) => {
    mediaAvailable.value = hasMediaAvailable;
  };

  const getPrevTrack = (track: Track) => {
    if (track) {
      const currentIndex = mediaState.value.currentPageTracks.findIndex((t) => t.text.src === track.text.src);
      return mediaState.value.currentPageTracks[currentIndex - 1];
    }
    return mediaState.value.currentPageTracks[0];
  };

  const getNextTrack = (track: Track) => {
    if (track) {
      const currentIndex = mediaState.value.currentPageTracks.findIndex((t) => t.text.src === track.text.src);
      return mediaState.value.currentPageTracks[currentIndex + 1];
    }
    return mediaState.value.currentPageTracks[0];
  };

  const clearTracks = () => {
    mediaState.value.currentPageTracks = [];
    mediaState.value.spineItemsTracks = [];
  };

  const addTracks = (tracks: Track[], spineIndex: number) => {
    // Use spine item index to store tracks in correct order.
    spineItemsTracks.value[spineIndex] = tracks;
    mediaState.value.currentPageTracks = spineItemsTracks.value.flat();
  };

  const getTracksByHref = (href: string, id?: string): Track[] => {
    const searchHref = id ? `${href}#${id}` : href;
    return mediaState.value.currentPageTracks.filter((track) => track.text.src.replace('../', '') === searchHref);
  };

  const setCurrentTrack = (track?: Track) => {
    currentTrack.value = track;

    // Update position tracking with audio track position
    // Only update position when:
    // 1. No track (clearing), OR
    // 2. Not currently playing (safe to update), OR
    // 3. User is manually changing track (intentional action), OR
    // 4. Auto-follow is enabled (narration guides the view)
    // This prevents unwanted scrolling when audio auto-progresses while user is viewing elsewhere
    if (!track) {
      setAudioTrackPosition(undefined);
    } else if (!playing.value || isUserChangingTrack.value || isAutoFollowPlayback.value) {
      // Use the track's src (which includes the fragment ID for precise positioning)
      setAudioTrackPosition(track.text.src, track.cfi);
    }
    // Otherwise: audio is auto-progressing AND auto-follow is OFF, don't update position (prevents scroll jump)
  };

  const setLastPlayedBeginTime = (beginTime: number) => {
    mediaState.value.lastPlayedBeginTime = beginTime;
  };
  const setCurrentPlayerTime = (time: number) => {
    mediaState.value.currentPlayerTime = time;
  };
  const setAudioDuration = (duration: number) => {
    mediaState.value.audioDuration = duration;
  };

  const setNoMediaPageSkip = (pageSkip: any) => {
    mediaState.value.noMediaPageSkip = pageSkip;
  };

  const setAudioLanguages = (_audioLanguages: string[]) => {
    mediaState.value.audioLanguages = _audioLanguages;
  };
  const setVideoLanguages = (_videoLanguages: string[]) => {
    mediaState.value.videoLanguages = _videoLanguages;
  };
  const setPreferredLanguage = (_preferredLanguage?: string) => {
    mediaState.value.preferredLanguage =
      _preferredLanguage !== mediaState.value.mainLanguage ? _preferredLanguage : undefined;

    videoLanguage.value = mediaState.value.videoLanguages.includes(_preferredLanguage || '');
  };
  const setMainLanguage = (_mainLanguage?: string) => {
    mediaState.value.mainLanguage = _mainLanguage;
  };

  const setSecondaryLanguage = (_secondaryLanguage?: string) => {
    mediaState.value.secondaryLanguage = _secondaryLanguage;
  };

  const setTextLanguages = (languageCodes: string[]) => {
    mediaState.value.otherTextLanguagesCodes = languageCodes;
  };

  const setPictureInPicture = (_pictureInPicture: string[]) => {
    mediaState.value.pictureInPicture = _pictureInPicture;
  };

  const setVideoPosition = (value: VideoPosition) => {
    mediaState.value.videoPosition = value;
  };
  const setVideoSize = (value: VideoSize) => {
    mediaState.value.videoSize = value;
  };

  const $reset = () => {
    mediaState.value = { ...defaultMediaState };
    playing.value = false;
    mediaAvailable.value = false;
    currentTrack.value = undefined;
    videoLanguage.value = false;
    lastTrack.value = false;
    isUserChangingTrack.value = false;
    totalContinuousBookAudioDuration.value = 0;
    continuousBookAudioTrackTimings.value = new Map();
  };

  const resetState = () => {
    $reset();
  };

  const switchToTheNextPage = () => {
    if (hasAutoPageTurn.value) {
      const pageDuration = 3000;

      console.debug(`No audio track detected, going to next spine in ${pageDuration}ms`);
      setNoMediaPageSkip(true);
      doWithAutoPageTurnTimeout(() => {
        if (isPlaying.value && mediaState.value.noMediaPageSkip) {
          goToNextSpine();
        }
      }, pageDuration);
    }
  };

  const setFirstTrackInPageAsCurrent = () => {
    setCurrentTrack(mediaState.value.currentPageTracks[0]);
  };

  const resetRequestedPlayerPosition = () => {
    requestedPlayerPosition.value = undefined;
  };

  const setIsLastTrack = (isLastTrack: boolean) => {
    lastTrack.value = isLastTrack;
  };

  const setIsUserChangingTrack = (value: boolean) => {
    isUserChangingTrack.value = value;
  };

  const setSkipFeedback = (value: string | undefined) => {
    skipFeedback.value = value;
  };

  const setAudioScrubbingTime = (time: number | undefined) => {
    audioScrubbingTime.value = time;
  };

  const setActiveHighlightCfi = (cfi: string | null) => {
    activeHighlightCfi.value = cfi;
  };

  const trackAnalyticsKey = (track: Track | undefined): string | undefined =>
    track ? `${track.audio.src}|${track.audio.clipBegin ?? ''}|${track.audio.clipEnd ?? ''}` : undefined;

  const setTotalBookAudioDuration = (duration: number) => {
    totalContinuousBookAudioDuration.value = duration;
  };

  const setBookAudioTrackTimings = (timings: Map<string, { start: number; end: number }>) => {
    continuousBookAudioTrackTimings.value = timings;
  };

  const getMediaSettingsForAnalytics = () => {
    return {
      isPlaying: isPlaying.value.toString(),
      audioLanguage: audioLanguage.value?.toString() ?? 'Not specified',
      mainLanguage: mainLanguage.value?.toString() ?? 'Not specified',
      preferredLanguage: preferredLanguage.value?.toString() ?? 'Not specified',
    };
  };

  return {
    playing,
    isPlaying,
    currentPageTracks,
    currentTrack,
    isCurrentTrack,
    getNextTrackOnPage,
    getPrevTrackOnPage,
    getUniqueNextTrackOnPage,
    hasMediaAvailable,
    isCurrentClipOnCurrentPage,
    isCurrentTrackOnCurrentPage,
    lastPlayedBeginTime,
    audioLanguage,
    audioLanguages,
    videoLanguages,
    isVideoLanguage,
    isPlayingMainLanguage,
    mainLanguage,
    secondaryLanguage,
    otherTextLanguagesCodes,
    preferredLanguage,
    pictureInPicture,
    hasPictureInPicture,
    videoPosition,
    videoSize,
    currentPlayerTime,
    audioDuration,
    isAtStartOfMedia,
    isAtEndOfMedia,
    requestedPlayerPosition,
    skipFeedback,
    audioScrubbingTime,
    isLastTrack,
    isUserChangingTrack,
    activeHighlightCfi,
    addTracks,
    clearTracks,
    resetState,
    setCurrentTrack,
    setFirstTrackInPageAsCurrent,
    setLastPlayedBeginTime,
    setMediaAvailable,
    setNoMediaPageSkip,
    setMainLanguage,
    setSecondaryLanguage,
    setTextLanguages,
    setPictureInPicture,
    setPreferredLanguage,
    setVideoLanguages,
    setVideoPosition,
    setAudioLanguages,
    setVideoSize,
    switchToTheNextPage,
    getTracksByHref,
    getPrevTrack,
    getNextTrack,
    setCurrentPlayerTime,
    setAudioDuration,
    resetRequestedPlayerPosition,
    setIsLastTrack,
    setIsUserChangingTrack,
    setSkipFeedback,
    setAudioScrubbingTime,
    setActiveHighlightCfi,
    getMediaSettingsForAnalytics,
    trackAnalyticsKey,
    totalContinuousBookAudioDuration,
    setTotalBookAudioDuration,
    continuousBookAudioTrackTimings,
    setBookAudioTrackTimings,
  };
});

export default useMediaStore;
